import {
    createContext,
    useContext,
    useMemo,
    useState,
    useCallback,
    useEffect,
    useRef,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from "react";
import { getSuggestedCuts } from "../queries/editQueries";
import { TimelineTrack } from "../reactvideoeditor/pro/components/advanced-timeline/types";
import { FPS } from "../constants";
import { Overlay } from "../reactvideoeditor/pro/types";

export type SuggestedCutType =
    | "silence"
    | "shot_change"
    | "replay"
    | "replay_logo";

export interface SuggestedCut<T extends SuggestedCutType = SuggestedCutType> {
    type: T;
    stream_time: number;
    start: number;
    end: number;
}

interface SuggestedCutsContextType {
    silence: SuggestedCut<"silence">[];
    shot_change: SuggestedCut<"shot_change">[];
    replay: SuggestedCut<"replay">[];
    replay_logo: SuggestedCut<"replay_logo">[];

    setSilence: Dispatch<SetStateAction<SuggestedCut<"silence">[]>>;
    setShotChange: Dispatch<SetStateAction<SuggestedCut<"shot_change">[]>>;
    setReplay: Dispatch<SetStateAction<SuggestedCut<"replay">[]>>;
    setReplayLogo: Dispatch<SetStateAction<SuggestedCut<"replay_logo">[]>>;

    clearSuggestedCuts: () => void;
    loadSuggestedCuts: (eventId: string, start: number, end: number) => void;

    showSuggestedCuts: boolean;
    setShowSuggestedCuts: Dispatch<SetStateAction<boolean>>;
    /** Whether resizing a segment snaps to the visible suggested cuts (only effective while cuts are shown). */
    snapToCutsEnabled: boolean;
    setSnapToCutsEnabled: Dispatch<SetStateAction<boolean>>;
    updateSuggestedCutsFromTracks: (tracks: TimelineTrack[], eventId: string, newStart?: number, newEnd?: number) => void;
    updateSuggestedCutsFromOverlays: (eventId: string, newStart: number, newEnd: number, overlays: Overlay[]) => void;

    renderedSuggestedCuts: SuggestedCut[];
    setRenderedSuggestedCuts: Dispatch<SetStateAction<SuggestedCut[]>>;
}

// Lower value = higher priority when clustering
const CUT_PRIORITY: Record<SuggestedCutType, number> = {
    silence: 0,
    shot_change: 1,
    replay_logo: 2,
    replay: 3,
};

/**
 * Keeps at most one cut per `minGapSeconds` window. Cuts are picked by type priority
 * (silence > shot_change > replay_logo > replay), then by time; a cut is dropped
 * if a higher-priority one was already kept within the gap.
 */
export const clusterSuggestedCuts = (
    cuts: SuggestedCut[],
    minGapSeconds: number
): SuggestedCut[] => {
    const mid = (c: SuggestedCut) => (c.start + c.end) / 2;
    const sorted = [...cuts].sort(
        (a, b) => CUT_PRIORITY[a.type] - CUT_PRIORITY[b.type] || mid(a) - mid(b)
    );
    const kept: SuggestedCut[] = [];
    for (const cut of sorted) {
        if (!kept.some(k => Math.abs(mid(k) - mid(cut)) < minGapSeconds)) {
            kept.push(cut);
        }
    }
    return kept;
};

export const SuggestedCutsContext = createContext<SuggestedCutsContextType | undefined>(undefined);

export const SuggestedCutsProvider = ({ children }: { children: ReactNode }) => {
    const [silence, setSilence] = useState<SuggestedCut<"silence">[]>([]);
    const [shot_change, setShotChange] = useState<SuggestedCut<"shot_change">[]>([]);
    const [replay, setReplay] = useState<SuggestedCut<"replay">[]>([]);
    const [replay_logo, setReplayLogo] = useState<SuggestedCut<"replay_logo">[]>([]);

    const [showSuggestedCuts, setShowSuggestedCuts] = useState<boolean>(true);
    const [snapToCutsEnabled, setSnapToCutsEnabled] = useState<boolean>(true);
    const [renderedSuggestedCuts, setRenderedSuggestedCuts] = useState<SuggestedCut[]>([]);

    // Store the pending request, so only the most recently started request can update the state.
    const latestRequestIdRef = useRef(0);
    const activeRequestRef = useRef<{
        id: number;
        promise: ReturnType<typeof getSuggestedCuts>;
    } | null>(null);

    const loadSuggestedCuts = useCallback(async (eventId: string, start: number, end: number) => {
        const requestId = ++latestRequestIdRef.current;

        try {
            const promise = getSuggestedCuts(eventId, start, end);
            activeRequestRef.current = { id: requestId, promise };

            const data = await promise;

            // Another load, clear or unmount happened: ignore this response.
            if (requestId !== latestRequestIdRef.current) return;

            const landmarks = data.landmarks;
            setSilence(landmarks.silence ?? []);
            setShotChange(landmarks.shot_change ?? []);
            setReplay(landmarks.replay ?? []);
            setReplayLogo(landmarks.replay_logo ?? []);
        } catch (error) {
            // Ignore failures from obsolete requests too.
            if (requestId === latestRequestIdRef.current) {
                console.error("Failed to load suggested cuts:", error);
            }
        } finally {
            // An older request must not erase the reference to a newer one.
            if (activeRequestRef.current?.id === requestId) {
                activeRequestRef.current = null;
            }
        }
    }, []);

    const clearSuggestedCuts = useCallback(() => {
        // Make every pending response obsolete before clearing the state.
        latestRequestIdRef.current += 1;
        activeRequestRef.current = null;
        setSilence([]);
        setShotChange([]);
        setReplay([]);
        setReplayLogo([]);
    }, []);

    useEffect(() => {
        return () => {
            // Prevent state updates from requests completing after unmount.
            latestRequestIdRef.current += 1;
            activeRequestRef.current = null;
        };
    }, []);

    const updateSuggestedCutsFromTracks = useCallback((
        tracks: TimelineTrack[],
        eventId: string,
        newStart?: number,
        newEnd?: number
    ) => {
        const items = tracks.flatMap(track => track.items);
        const hasNewRange = newStart !== undefined && newEnd !== undefined;

        if (items.length === 0) {
            if (hasNewRange) {
                loadSuggestedCuts(eventId, newStart, newEnd);
            } else {
                clearSuggestedCuts();
            }
            return;
        }

        if (items.length === 1 && hasNewRange) {
            loadSuggestedCuts(eventId, newStart, newEnd);
            return
        }

        const currentStart = Math.min(...items.map(item => item.start));
        const currentEnd = Math.max(...items.map(item => item.end));

        if (!hasNewRange) {
            loadSuggestedCuts(eventId, currentStart, currentEnd);
            return;
        }

        const finalStart = Math.min(currentStart, newStart);
        const finalEnd = Math.max(currentEnd, newEnd);

        // Keep the original behavior of loading when the first item is present,
        // but do it only once (the previous implementation could load twice).
        if (finalStart !== currentStart || finalEnd !== currentEnd) {
            loadSuggestedCuts(eventId, finalStart, finalEnd);
        }
    }, [loadSuggestedCuts, clearSuggestedCuts]);

    const updateSuggestedCutsFromOverlays = useCallback((
        eventId: string,
        newStart: number,
        newEnd: number,
        overlays: Overlay[]
    ) => {
        if (overlays.length === 0) {
            loadSuggestedCuts(eventId, newStart, newEnd);
            return;
        }

        const currentStart = Math.min(...overlays.map(o => o.from)) / FPS;
        const currentEnd = Math.max(...overlays.map(o => o.from + o.durationInFrames)) / FPS;

        const finalStart = Math.min(currentStart, newStart);
        const finalEnd = Math.max(currentEnd, newEnd);

        if (finalStart !== currentStart || finalEnd !== currentEnd) {
            loadSuggestedCuts(eventId, finalStart, finalEnd);
        }
    }, [loadSuggestedCuts]);

    const value = useMemo(
        () => ({
            silence,
            shot_change,
            replay,
            replay_logo,
            setSilence,
            setShotChange,
            setReplay,
            setReplayLogo,
            clearSuggestedCuts,
            loadSuggestedCuts,
            showSuggestedCuts,
            setShowSuggestedCuts,
            snapToCutsEnabled,
            setSnapToCutsEnabled,
            updateSuggestedCutsFromTracks,
            updateSuggestedCutsFromOverlays,
            renderedSuggestedCuts,
            setRenderedSuggestedCuts
        }),
        [
            silence,
            shot_change,
            replay,
            replay_logo,
            showSuggestedCuts,
            snapToCutsEnabled,
            clearSuggestedCuts,
            loadSuggestedCuts,
            updateSuggestedCutsFromTracks,
            updateSuggestedCutsFromOverlays,
            renderedSuggestedCuts
        ]
    );

    return (
        <SuggestedCutsContext.Provider value={value}>
            {children}
        </SuggestedCutsContext.Provider>
    );
};

export const useSuggestedCuts = () => {
    const context = useContext(SuggestedCutsContext);

    if (!context) {
        throw new Error("useSuggestedCuts must be used within a SuggestedCutsProvider");
    }

    return context;
};
